import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function DataTable({ 
  columns, 
  data, 
  onRowClick,
  actions,
  emptyMessage = 'No data found',
  isLoading = false,
  mobileCardRenderer = null // Optional custom mobile card renderer
}) {
  if (isLoading) {
    return (
      <div className="bg-white border border-stone-200 rounded-lg p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-900"></div>
          <span className="ml-2 text-stone-500">Loading...</span>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white border border-stone-200 rounded-lg p-8">
        <div className="empty-state">
          <p className="text-stone-500">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  // Normalize columns to support both formats:
  // Old format: { key, label, render, width }
  // TanStack format: { accessorKey, header, cell }
  const normalizedColumns = columns.map(col => ({
    key: col.key || col.accessorKey,
    label: col.label || col.header,
    render: col.render || (col.cell ? (value, row) => col.cell({ row: { original: row }, getValue: () => value }) : null),
    width: col.width,
    mobileHidden: col.mobileHidden || false,
    mobileLabel: col.mobileLabel || col.label || col.header
  }));

  // Get key columns for mobile display (first 3-4 columns that aren't hidden)
  const mobileColumns = normalizedColumns.filter(col => !col.mobileHidden).slice(0, 4);

  // Default mobile card renderer
  const renderMobileCard = (row, rowIndex) => {
    if (mobileCardRenderer) {
      return mobileCardRenderer(row, rowIndex, actions);
    }

    return (
      <div 
        key={row.id || rowIndex}
        className={`border rounded-lg p-4 space-y-3 bg-white ${onRowClick ? 'cursor-pointer active:bg-stone-50' : ''}`}
        onClick={() => onRowClick && onRowClick(row)}
        data-testid={`mobile-card-${row.id || rowIndex}`}
      >
        {/* Primary info - first column usually the ID/name */}
        {mobileColumns[0] && (
          <div className="flex items-start justify-between">
            <div className="font-medium text-stone-900">
              {mobileColumns[0].render 
                ? mobileColumns[0].render(row[mobileColumns[0].key], row) 
                : row[mobileColumns[0].key]
              }
            </div>
            {actions && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" className="h-8 w-8 -mr-2 -mt-1">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {(typeof actions === 'function' ? actions(row) : actions).map((action, index) => (
                    <DropdownMenuItem
                      key={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        action.onClick(row);
                      }}
                      className={action.destructive ? 'text-red-600' : (action.className || '')}
                    >
                      {action.icon && <action.icon className={`mr-2 h-4 w-4 ${action.className || ''}`} />}
                      {action.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )}

        {/* Secondary columns */}
        <div className="grid grid-cols-2 gap-2 text-sm">
          {mobileColumns.slice(1).map((column, idx) => (
            <div key={column.key}>
              <span className="text-stone-500 text-xs">{column.mobileLabel}</span>
              <div className="text-stone-700">
                {column.render ? column.render(row[column.key], row) : row[column.key] || '-'}
              </div>
            </div>
          ))}
        </div>

        {/* Show remaining important columns if any */}
        {normalizedColumns.length > 4 && (
          <div className="pt-2 border-t border-stone-100 flex flex-wrap gap-2">
            {normalizedColumns.slice(4).filter(col => !col.mobileHidden).map((column) => {
              const value = column.render ? column.render(row[column.key], row) : row[column.key];
              if (!value || value === '-') return null;
              return (
                <div key={column.key} className="text-xs">
                  <span className="text-stone-400">{column.mobileLabel}: </span>
                  <span className="text-stone-600">{value}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
      {/* Mobile Card View */}
      <div className="md:hidden p-3 space-y-3">
        {data.map((row, rowIndex) => renderMobileCard(row, rowIndex))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50 hover:bg-stone-50">
              {normalizedColumns.map((column) => (
                <TableHead 
                  key={column.key} 
                  className="text-xs font-medium text-stone-500 uppercase tracking-wider py-3"
                  style={{ width: column.width }}
                >
                  {column.label}
                </TableHead>
              ))}
              {actions && <TableHead className="w-12"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, rowIndex) => (
              <TableRow 
                key={row.id || rowIndex}
                className={`${onRowClick ? 'cursor-pointer hover:bg-stone-50' : ''}`}
                onClick={() => onRowClick && onRowClick(row)}
                data-testid={`table-row-${row.id || rowIndex}`}
              >
                {normalizedColumns.map((column) => (
                  <TableCell key={column.key} className="py-3 text-sm">
                    {column.render ? column.render(row[column.key], row) : row[column.key]}
                  </TableCell>
                ))}
                {actions && (
                  <TableCell className="py-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`actions-btn-${row.id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {(typeof actions === 'function' ? actions(row) : actions).map((action, index) => (
                          <DropdownMenuItem
                            key={index}
                            onClick={(e) => {
                              e.stopPropagation();
                              action.onClick(row);
                            }}
                            className={action.destructive ? 'text-red-600' : (action.className || '')}
                            data-testid={`action-${action.label.toLowerCase().replace(/\s+/g, '-')}-${row.id}`}
                          >
                            {action.icon && <action.icon className={`mr-2 h-4 w-4 ${action.className || ''}`} />}
                            {action.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
